import React, { useState, useEffect } from 'react';

// Icon components
const PlusIcon = ({ className = "w-4 h-4" }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
  </svg>
);

const TrashIcon = ({ className = "w-4 h-4" }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
  </svg>
);

const PencilIcon = ({ className = "w-4 h-4" }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
  </svg>
);

export interface ProductDefect {
  id?: string;
  defect_name: string;
  defect_code?: string;
  severity: number;
  description?: string;
  isNew?: boolean; // To track new defects before saving
  isDeleted?: boolean; // To track deleted defects
}

interface ProductDefectsSectionProps {
  productId?: string;
  isNewProduct: boolean;
  onDefectsChange: (defects: ProductDefect[]) => void;
}

const ProductDefectsSection: React.FC<ProductDefectsSectionProps> = ({ 
  productId, 
  isNewProduct,
  onDefectsChange 
}) => {
  const [defects, setDefects] = useState<ProductDefect[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  
  // Form state
  const [defectName, setDefectName] = useState('');
  const [defectCode, setDefectCode] = useState('');
  const [severity, setSeverity] = useState(5);
  const [description, setDescription] = useState('');

  // Load existing defects if editing a product
  useEffect(() => {
    // Only fetch if we have a real UUID (not internal ID)
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!isNewProduct && productId && uuidRegex.test(productId)) {
      fetchDefects();
    }
  }, [productId, isNewProduct]);

  const fetchDefects = async () => {
    if (!productId) return;
    
    try {
      const response = await fetch(`/api/products/${productId}/defects`);
      if (response.ok) {
        const data = await response.json();
        setDefects(data);
        onDefectsChange(data);
      }
    } catch (error) {
      console.error('Error fetching defects:', error);
    }
  };

  const handleAddDefect = () => {
    const newDefect: ProductDefect = {
      defect_name: defectName,
      defect_code: defectCode || undefined,
      severity,
      description: description || undefined,
      isNew: true
    };
    
    const updatedDefects = [...defects, newDefect];
    setDefects(updatedDefects);
    onDefectsChange(updatedDefects);
    
    // Reset form
    setDefectName('');
    setDefectCode('');
    setSeverity(5);
    setDescription('');
    setShowAddForm(false);
  };

  const handleUpdateDefect = (index: number, updated: ProductDefect) => {
    const updatedDefects = defects.map((d, i) => 
      i === index ? { ...updated, isNew: d.isNew } : d
    );
    setDefects(updatedDefects);
    onDefectsChange(updatedDefects);
    setEditingIndex(null);
  };

  const handleDeleteDefect = (index: number) => {
    const defect = defects[index];
    let updatedDefects: ProductDefect[];
    
    if (defect.isNew) {
      // If it's a new defect that hasn't been saved, just remove it
      updatedDefects = defects.filter((_, i) => i !== index);
    } else {
      // Mark existing defect as deleted
      updatedDefects = defects.map((d, i) => 
        i === index ? { ...d, isDeleted: true } : d
      );
    }
    
    setDefects(updatedDefects);
    onDefectsChange(updatedDefects);
  };

  const getSeverityColor = (severity: number) => {
    if (severity >= 8) return 'bg-red-100 text-red-800';
    if (severity >= 5) return 'bg-yellow-100 text-yellow-800';
    return 'bg-green-100 text-green-800';
  };

  // Filter out deleted defects for display
  const visibleDefects = defects.filter(d => !d.isDeleted);

  // Check if this is an internal ID (not saved to database yet)
  const isInternalId = productId && !(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(productId));

  return (
    <div className="border-t pt-4 mt-4">
      <h3 className="text-sm font-medium text-slate-700 mb-3">Defect Definitions</h3>
      
      {isInternalId && (
        <div className="mb-3 p-2 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-800">
          ℹ️ Defects will be saved when the product is synced to the database
        </div>
      )}
      
      {/* Add New Defect Button/Form */}
      {!showAddForm ? (
        <button
          type="button"
          onClick={() => setShowAddForm(true)}
          className="mb-3 px-3 py-1.5 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center"
        >
          <PlusIcon className="h-4 w-4 mr-1" />
          Add Defect
        </button>
      ) : (
        <div className="mb-4 p-3 border rounded bg-gray-50">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Defect Name *
              </label>
              <input
                type="text"
                value={defectName}
                onChange={(e) => setDefectName(e.target.value)}
                className="w-full px-2 py-1 text-sm border rounded"
                placeholder="e.g., Bruising"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Code
              </label>
              <input
                type="text"
                value={defectCode}
                onChange={(e) => setDefectCode(e.target.value)}
                className="w-full px-2 py-1 text-sm border rounded"
                placeholder="e.g., BR-001"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Severity (1-10) *
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="range"
                  min="1"
                  max="10"
                  value={severity}
                  onChange={(e) => setSeverity(parseInt(e.target.value))}
                  className="flex-1"
                />
                <span className={`px-2 py-0.5 text-xs rounded-full font-semibold ${getSeverityColor(severity)}`}>
                  {severity}
                </span>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Description
              </label>
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full px-2 py-1 text-sm border rounded"
                placeholder="Brief description"
              />
            </div>
          </div>
          <div className="mt-3 flex justify-end space-x-2">
            <button
              type="button"
              onClick={() => {
                setShowAddForm(false);
                setDefectName('');
                setDefectCode('');
                setSeverity(5);
                setDescription('');
              }}
              className="px-3 py-1 text-sm border rounded hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleAddDefect}
              disabled={!defectName}
              className="px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-400"
            >
              Add
            </button>
          </div>
        </div>
      )}

      {/* Defects List */}
      {visibleDefects.length === 0 ? (
        <p className="text-sm text-gray-500 italic">No defects defined yet.</p>
      ) : (
        <div className="space-y-2">
          {visibleDefects.map((defect, index) => (
            <div key={index} className="border rounded p-2 text-sm">
              {editingIndex === index ? (
                // Edit mode
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="text"
                    value={defect.defect_name}
                    onChange={(e) => {
                      const updated = { ...defect, defect_name: e.target.value };
                      const updatedDefects = [...defects];
                      updatedDefects[index] = updated;
                      setDefects(updatedDefects);
                    }}
                    className="px-2 py-1 text-sm border rounded"
                  />
                  <div className="flex items-center space-x-1">
                    <input
                      type="range"
                      min="1"
                      max="10"
                      value={defect.severity}
                      onChange={(e) => {
                        const updated = { ...defect, severity: parseInt(e.target.value) };
                        const updatedDefects = [...defects];
                        updatedDefects[index] = updated;
                        setDefects(updatedDefects);
                      }}
                      className="flex-1"
                    />
                    <span className={`px-2 py-0.5 text-xs rounded-full font-semibold ${getSeverityColor(defect.severity)}`}>
                      {defect.severity}
                    </span>
                  </div>
                  <div className="col-span-2 flex justify-end space-x-1">
                    <button
                      type="button"
                      onClick={() => setEditingIndex(null)}
                      className="px-2 py-1 text-xs border rounded hover:bg-gray-100"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={() => handleUpdateDefect(index, defect)}
                      className="px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600"
                    >
                      Save
                    </button>
                  </div>
                </div>
              ) : (
                // View mode
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <span className="font-medium">{defect.defect_name}</span>
                    {defect.defect_code && (
                      <span className="text-gray-500 ml-2">({defect.defect_code})</span>
                    )}
                    <span className={`ml-2 px-2 py-0.5 text-xs rounded-full font-semibold ${getSeverityColor(defect.severity)}`}>
                      {defect.severity}
                    </span>
                    {defect.isNew && (
                      <span className="ml-2 px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded-full">
                        New
                      </span>
                    )}
                  </div>
                  <div className="flex space-x-1">
                    <button
                      type="button"
                      onClick={() => setEditingIndex(index)}
                      className="p-1 text-blue-600 hover:text-blue-800"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDeleteDefect(index)}
                      className="p-1 text-red-600 hover:text-red-800"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProductDefectsSection;